import React, { useEffect, useState } from 'react';
import { Badge, Button } from 'flowbite-react';
import { BsArrowLeft, BsArrowRight } from 'react-icons/bs';
import { RecommendTechStack } from './RecommendTechStack';
import { MeetingStatus, MeetingType, UIRoundTemplate } from 'types';
import { useUserContext } from 'src/context/UserProvider';
import { findRemainingAttempts, isUserCollegeUser } from 'shared-utils';
import { RecommendRound } from './RecommendRound';
import { GoDotFill } from 'react-icons/go';
import classNames from 'classnames';
import CaptureAndUploadProfileImage from '../profile/CaptureAndUploadProfileImage';
import { browserName, isMobile } from 'react-device-detect';
import BrowserWarningModal from './BrowserWarningModal';
import { Img } from 'react-image';

interface Props {
	roundTemplates: UIRoundTemplate[];
	setSelectedTechStack: (val: string) => void;
	selectedTeckStack: string;
	currentStep: number;
	setCurrentStep: (val: number) => void;
	selectedRound: string;
	setSelectedRound: (val: string) => void;
	setIsAIRoundSelected: (val: boolean) => void;
	showMockTestOnly?: boolean;
	setShowMockTestOnly?: React.Dispatch<React.SetStateAction<boolean>>;
	interviewType?: MeetingType | null;
	setInterviewType?: React.Dispatch<React.SetStateAction<MeetingType | null>>;
	onNext?: () => void;
}

export const TeckStackStep = ({
	currentStep,
	roundTemplates,
	selectedTeckStack,
	setCurrentStep,
	setSelectedTechStack,
	selectedRound,
	setSelectedRound,
	setIsAIRoundSelected,
	showMockTestOnly,
	setShowMockTestOnly,
	interviewType,
	setInterviewType,
	onNext,
}: Props) => {
	const { userRounds, hasReschedulableMeeting, userMeetings, user, showAwaitingResults } = useUserContext();
	const [availableRounds, setAvailableRounds] = useState(userRounds);
	const [openPorfileUploadModal, setOpenProfileUploadModal] = useState(false);
	const [openBrowserWarningModal, setOpenBrowserWarningModal] = useState(false);

	const totalMockAttempts = userMeetings?.filter(
		meeting => meeting?.isMock && [MeetingStatus.COMPLETED].includes(meeting.meetingStatus),
	)?.length;

	const disableStartInterView =
		availableRounds?.length ||
		roundTemplates?.filter(
			el =>
				(interviewType === MeetingType.Assessment
					? MeetingType.Assessment === el?.templateType
					: el.templateType !== MeetingType.Assessment) && (showMockTestOnly ? el.isMock : !el.isMock),
		)?.length
			? false
			: true;

	const interviewTypes = [
		{
			title: 'Profile Picture',
			subTitle: 'Complete your profile by uploading your picture.',
			onClick: () => setOpenProfileUploadModal(true),
			ctaText: 'Upload',
			hide: user?.profile?.proctoringImageUrl,
			disabled: false,
			completed: user?.profile?.proctoringImageUrl,
		},
		{
			title: 'AI Interview',
			subTitle: 'The AI interview test is a 90-minute assessment designed to evaluate your skills comprehensively.',
			onClick: () => {
				browserName !== 'Chrome' && !isMobile
					? setOpenBrowserWarningModal(true)
					: setInterviewType && setInterviewType(MeetingType.Interview);
			},
			warning:
				browserName !== 'Chrome' && !isMobile ? (
					<div className="hidden md:flex items-center gap-2">
						<Img src="/assets/chromeLogo.svg" className="h-[32px] w-[32px]" />
						<p className="max-w-[195px] text-[10px] font-[400] text-[#555770] dark:text-gray-300">
							Please install the latest Chrome Browser for the interview.{' '}
							<a
								className="text-[#7777E7] dark:text-blue-400 "
								href="https://www.google.com/intl/en_in/chrome/"
								target="_blank"
							>
								Use Chrome
							</a>
						</p>
					</div>
				) : null,
			ctaText: 'Start Interview',
			disabled: !user?.profile?.proctoringImageUrl,
			pending: showAwaitingResults,
			completed:
				userMeetings?.filter(
					el => el.isAIInterview && !el.isMock && [MeetingStatus.COMPLETED].includes(el.meetingStatus),
				)?.length &&
				userRounds.filter(
					r => findRemainingAttempts(r.maxAttempts, r.meetings) && !r.isMock && r.roundTemplate.isAIInterviewTemplate,
				)?.length === 0,
		},
		{
			title: 'Assessment Test',
			subTitle:
				'The 30-minute assessment test is designed to gauge your skills in puzzles, aptitude, reading comprehension, and data interpretation.',
			onClick: () => {
				setInterviewType && setInterviewType(MeetingType.Assessment);
			},
			ctaText: 'Take Test',
			disabled: !user?.profile?.proctoringImageUrl,
			completed:
				userMeetings?.filter(
					el => !el.isAIInterview && !el.isMock && [MeetingStatus.COMPLETED].includes(el.meetingStatus),
				)?.length &&
				userRounds.filter(
					r => findRemainingAttempts(r.maxAttempts, r.meetings) && !r.isMock && !r.roundTemplate.isAIInterviewTemplate,
				)?.length === 0,
		},
	];

	useEffect(() => {
		const filteredRound = userRounds.filter(
			r =>
				findRemainingAttempts(r.maxAttempts, r.meetings) &&
				(showMockTestOnly ? r.isMock : !r?.isMock) &&
				(!interviewType ||
					(interviewType === MeetingType.Assessment
						? interviewType === r.meetingType
						: r.meetingType !== MeetingType.Assessment)),
		);

		setAvailableRounds(filteredRound);
		if (filteredRound?.length && filteredRound[0]?._id) setSelectedRound(filteredRound[0]._id);
	}, [userRounds, interviewType, showMockTestOnly]);
	return (
		<div className="px-[14px] py-[16px] font-inter flex flex-col gap-4">
			{user && isUserCollegeUser(user) && !showMockTestOnly && !interviewType ? (
				<div className="flex flex-col h-full">
					<div className="flex flex-col">
						{interviewTypes
							?.filter(el => !el?.hide)
							?.map((el, index) => (
								<div key={index} className="flex gap-4">
									<div className="relative isolate">
										{el?.completed ? (
											<img
												alt="prepareBadgeCompleted"
												src="/assets/prepareBadgeCompleted.svg"
												className="min-w-6 min-h-6 bg-white dark:bg-[#111E2C]"
											/>
										) : (
											<img
												src="/assets/prepareBadgeInProgress.svg"
												alt="prepareBadgeInProgress"
												className="min-w-6 min-h-6"
											/>
										)}
										{index < interviewTypes?.filter(el => !el?.hide)?.length - 1 ? (
											<div
												className={classNames(
													'w-[2px] absolute inset-x-0 -z-[2] border-l-[2px] border-dashed border-[#6698FA] mx-auto',
													'h-full top-1',
												)}
											></div>
										) : null}
									</div>
									<div className="flex flex-col pb-[40px]">
										<div className="text-[16px] font-[500] text-[#1C1C28] dark:text-white">{el?.title}</div>
										<div className="mt-1 text-[#555770] dark:text-gray-400 font-[400] text-[12px] max-w-[412px]">
											{el?.subTitle}
										</div>

										{el?.pending ? (
											<Badge
												size="xs"
												color="warning"
												className="w-fit px-[10px] mt-2"
												icon={() => <GoDotFill className="scale-[1.5]" />}
											>
												Result Pending
											</Badge>
										) : el?.completed ? (
											<Badge
												color="green"
												className="w-fit mt-2"
												icon={() => <GoDotFill className="scale-[1.5] text-[#0E9F6E]" />}
											>
												Completed
											</Badge>
										) : (
											<div className="flex items-center gap-3 mt-4">
												<Button
													color="purplePlaced"
													className="max-w-[167px]"
													disabled={el.disabled}
													onClick={el?.onClick}
												>
													{el?.ctaText}
												</Button>

												{el?.warning ? el?.warning : null}
											</div>
										)}
									</div>
								</div>
							))}
					</div>
					{!(totalMockAttempts >= 2) ? (
						<div className="text-[#8F90A6] font-[500] text-[14px] pb-[8px]">
							Not confident yet?{' '}
							<button
								className="text-[#7777E7] underline"
								onClick={() => setShowMockTestOnly && setShowMockTestOnly(true)}
							>
								Try Mock Test
							</button>
						</div>
					) : null}
				</div>
			) : (
				<>
					{availableRounds.length ? (
						!hasReschedulableMeeting && (
							<div>
								<RecommendRound
									interviewType={interviewType}
									rounds={availableRounds}
									showMockTestOnly={showMockTestOnly}
									setSelectedRound={setSelectedRound}
									setIsAIRoundSelected={setIsAIRoundSelected}
									selectedRound={selectedRound}
								/>
							</div>
						)
					) : !hasReschedulableMeeting ? (
						<div>
							<RecommendTechStack
								interviewType={interviewType}
								roundTemplates={roundTemplates}
								showMockTestOnly={showMockTestOnly}
								setSelectedTechStack={setSelectedTechStack}
								setIsAIRoundSelected={setIsAIRoundSelected}
								selectedTeckStack={selectedTeckStack}
							/>
						</div>
					) : null}
					<div className="w-full flex items-center gap-4 justify-between mt-4">
						{user && isUserCollegeUser(user) && !showMockTestOnly && (
							<Button
								color="purplePlaced"
								onClick={() => setInterviewType && setInterviewType(null)}
								className="border py-1 border-[#5B8DEF] bg-transparent hover:text-white text-[#7777E7] dark:text-white"
							>
								<BsArrowLeft />
							</Button>
						)}
						<Button
							color="purplePlaced"
							disabled={(!selectedTeckStack && !selectedRound) || disableStartInterView}
							className="bg-[#7777E7] w-full text-white"
							onClick={() => {
								onNext && onNext();
							}}
						>
							Next{' '}
							<span className="inline pl-2">
								<BsArrowRight />
							</span>
						</Button>
					</div>
				</>
			)}

			<CaptureAndUploadProfileImage isOpen={openPorfileUploadModal} onClose={() => setOpenProfileUploadModal(false)} />
			<BrowserWarningModal
				openModal={openBrowserWarningModal}
				setOpenModal={() => {
					setOpenBrowserWarningModal(false);
				}}
				isJoinedFromMobile={isMobile}
			/>
		</div>
	);
};
